/**
 * AmountSelectionModal Component
 *
 * Modal for selecting service amount with suggested amounts and custom input
 */

import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ServiceType } from '@/types/service.types';
import clsx from 'clsx';

interface AmountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: ServiceType;
  onAddToCart: (amount: number) => void;
}

export function AmountSelectionModal({
  isOpen,
  onClose,
  serviceType,
  onAddToCart,
}: AmountSelectionModalProps) {
  const [customAmount, setCustomAmount] = useState<string>('');

  // Parse suggested amounts from semicolon delimited string
  const suggestedAmounts = serviceType.suggestedAmounts
    ? String(serviceType.suggestedAmounts)
        .split(';')
        .map((amt) => parseFloat(amt.trim()))
        .filter((amt) => !isNaN(amt) && amt > 0)
    : [50, 100, 250, 500];

  const handleAmountClick = (amount: number) => {
    setCustomAmount(amount.toString());
  };

  const handleAddToCart = () => {
    const amount = parseFloat(customAmount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Convert to cents
    const amountInCents = Math.round(amount * 100);
    onAddToCart(amountInCents);

    // Reset and close
    setCustomAmount('');
    onClose();
  };

  const handleClose = () => {
    setCustomAmount('');
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <Dialog.Title as="h3" className="text-xl font-bold text-gray-900 leading-6">
                      {serviceType.description ||
                        serviceType.shortName ||
                        `Service Type ${serviceType.donationType}`}
                    </Dialog.Title>
                    {serviceType.detailedText && (
                      <p className="mt-2 text-sm text-gray-600">{serviceType.detailedText}</p>
                    )}
                  </div>
                  <button
                    onClick={handleClose}
                    className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Suggested Amounts */}
                {suggestedAmounts.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Suggested Amounts
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {suggestedAmounts.map((amount, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleAmountClick(amount)}
                          className={clsx(
                            'py-3 px-4 rounded-lg font-semibold transition-all',
                            customAmount === amount.toString()
                              ? 'bg-emerald-600 text-white'
                              : 'bg-emerald-50 text-gray-900 hover:bg-emerald-100'
                          )}
                        >
                          ${amount.toFixed(2)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Amount Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {suggestedAmounts.length > 0 ? 'Or Enter Custom Amount' : 'Enter Amount'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
                      $
                    </span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="0.00"
                      min="1"
                      step="0.01"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-lg"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Add to Cart
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
